import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    AppBar,
    Toolbar,
    IconButton,
    Fab,
    useTheme as useMuiTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack"; // For back button
import PersonIcon from "@mui/icons-material/Person"; // Default avatar
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"; // <-- Import Icon for button
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { RootState } from "../../store/store";
import { fetchMessages, setActiveConversation } from "../../store/slices/messagingSlice";
import { Message } from "../../types/messaging";
import MessageInput from "./MessageInput"; // Assuming MessageInput handles sending
import { formatDistanceToNowStrict, isToday, format } from "date-fns"; // For timestamp formatting
import { fetchProfile, selectUserProfileById } from "../../store/slices/profileSlice";
import useResponsive from "../../hooks/useResponsive";
import { useNavigate } from "react-router-dom";

// --- Constants ---
// Defines the interval (in milliseconds) for checking for new messages.
// Making this a constant allows easy adjustment of polling frequency later.
const MESSAGE_POLLING_INTERVAL_MS = 5000; // 5 seconds
const SCROLL_THRESHOLD = 100; // Pixels from bottom to trigger auto-scroll

// --- Helper Functions ---

// Helper function to format timestamps for display within the chat window
const formatMessageTimestamp = (timestamp: number | undefined): string => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    try {
        if (isToday(date)) {
            // If the message is from today, show only the time (e.g., 10:30 AM)
            return format(date, "p");
        } else {
            // If the message is older, show date and time (e.g., Jul 20, 10:30 AM)
            return format(date, "MMM d, p");
        }
    } catch (e) {
        console.error("Error formatting message date:", e);
        return date.toLocaleTimeString(); // Fallback
    }
};

// --- Component ---

const ChatWindow: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const muiTheme = useMuiTheme();
    const messagesContainerRef = useRef<null | HTMLDivElement>(null); // Ref for the scrollable message container
    const messagesEndRef = useRef<null | HTMLDivElement>(null); // Ref to the bottom marker

    // --- ADD USE RESPONSIVE ---
    const { isTabletUp } = useResponsive();
    // --- END ADD USE RESPONSIVE ---

    // State for scroll-to-bottom button visibility
    const [showScrollDownButton, setShowScrollDownButton] = useState(false);
    // State to store visibility/opacity of each message
    const [messageOpacities, setMessageOpacities] = useState<{ [key: string]: number }>({});

    // Store scroll position before fetch
    const scrollPositionRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);
    // --- ADDED Ref to track initial load ---
    const isInitialLoad = useRef(true);
    // ---------------------------------------

    // Select necessary data from Redux store
    const activeConversationId = useAppSelector(
        (state: RootState) => state.messaging.activeConversationId,
    );
    // Use a memoized selector or derive state carefully to avoid unnecessary re-renders
    const {
        items: messages,
        loading,
        error,
    } = useAppSelector((state: RootState) => {
        // Find the messages state for the active conversation, or return default empty state
        return (
            state.messaging.messages[activeConversationId || ""] || {
                items: [],
                loading: "idle",
                error: null,
            }
        );
    });
    const currentUser = useAppSelector((state: RootState) => state.auth.user); // Get current user info
    const activeConversation = useAppSelector((state: RootState) =>
        state.messaging.conversations.items.find((c) => c.id === activeConversationId),
    );

    // Find the ID of the other participant
    const otherParticipantId = useMemo(
        () => activeConversation?.participants.find((p) => p !== currentUser?.id),
        [activeConversation?.participants, currentUser?.id],
    );

    // Fetch the other participant's profile
    useEffect(() => {
        if (otherParticipantId) {
            console.log(
                `[ChatWindow] Other participant ID: ${otherParticipantId}, fetching profile.`,
            );
            // Dispatch fetchProfile only if the ID is valid
            // profileSlice should handle not re-fetching if already loaded/loading
            dispatch(fetchProfile(otherParticipantId));
        }
    }, [otherParticipantId, dispatch]);

    // Get the other participant's profile data from the store
    const participantProfile = useAppSelector((state) =>
        otherParticipantId ? selectUserProfileById(state, otherParticipantId) : null,
    );
    const participantProfileLoading = useAppSelector((state: RootState) =>
        otherParticipantId
            ? state?.profile?.profiles?.[otherParticipantId]?.loading === "pending"
            : false,
    );

    // --- Effects ---

    // Effect for polling new messages
    useEffect(() => {
        let intervalId: ReturnType<typeof setInterval> | null = null; // Use ReturnType for correct type

        if (activeConversationId) {
            // --- Reset initial load flag when conversation changes ---
            isInitialLoad.current = true;
            // -------------------------------------------------------
            console.log(`[ChatWindow] Starting polling for conversation: ${activeConversationId}`);

            // Store scroll pos BEFORE initial fetch too
            if (messagesContainerRef.current) {
                const { scrollTop, scrollHeight } = messagesContainerRef.current;
                scrollPositionRef.current = { scrollTop, scrollHeight };
                console.log(
                    "[ChatWindow] Storing scroll pos (initial fetch):",
                    scrollPositionRef.current,
                );
            }

            dispatch(fetchMessages(activeConversationId));

            intervalId = setInterval(() => {
                // console.log(
                //     `[ChatWindow] Polling for new messages in conversation: ${activeConversationId}`,
                // );
                if (messagesContainerRef.current && loading !== "pending") {
                    // Check loading state here too
                    const { scrollTop, scrollHeight } = messagesContainerRef.current;
                    scrollPositionRef.current = { scrollTop, scrollHeight };
                    console.log(
                        "[ChatWindow] Storing scroll pos (poll):",
                        scrollPositionRef.current,
                    );
                }
                dispatch(fetchMessages(activeConversationId));
            }, MESSAGE_POLLING_INTERVAL_MS);
        }

        // Cleanup function: Clear interval when component unmounts or activeConversationId changes
        return () => {
            if (intervalId !== null) {
                // Check against null
                // console.log(
                //     `[ChatWindow] Stopping polling for conversation: ${activeConversationId}`,
                // );
                clearInterval(intervalId);
                scrollPositionRef.current = null; // Reset stored position on convo change
            }
        };
    }, [dispatch, activeConversationId]); // Removed 'loading' dependency here, handle pre-fetch scroll saving within the interval/initial dispatch logic

    // Effect to adjust scroll position AFTER messages potentially update
    useEffect(() => {
        // Only run if the container exists and we have a stored scroll position
        if (messagesContainerRef.current && scrollPositionRef.current && loading === "succeeded") {
            // Run only on success
            const container = messagesContainerRef.current;
            const { scrollTop: prevScrollTop, scrollHeight: prevScrollHeight } =
                scrollPositionRef.current;
            const newScrollHeight = container.scrollHeight;

            // Calculate how close to bottom user was BEFORE update
            const wasNearBottom =
                prevScrollHeight - prevScrollTop - container.clientHeight < SCROLL_THRESHOLD;

            console.log(
                "[ChatWindow] Adjusting scroll. Prev:",
                scrollPositionRef.current,
                "New Height:",
                newScrollHeight,
                "Was Near Bottom:",
                wasNearBottom,
                "Is Initial Load:",
                isInitialLoad.current,
            );

            // --- MODIFIED SCROLL LOGIC ---
            if (isInitialLoad.current) {
                // On initial load, scroll the container to the bottom.
                container.scrollTop = container.scrollHeight;
                // console.log("[ChatWindow] Initial load: Scrolling container to bottom.");
                isInitialLoad.current = false; // Mark initial load as done
            } else if (wasNearBottom && newScrollHeight > prevScrollHeight) {
                // If user was near bottom AND new messages arrived (height increased),
                // scroll the container to the bottom.
                container.scrollTop = container.scrollHeight;
                console.log(
                    "[ChatWindow] Poll update: User near bottom & new messages, scrolling container to bottom.",
                );
            } else if (newScrollHeight > prevScrollHeight) {
                // If user was scrolled up AND new messages added height, restore previous position relative to NEW height
                const scrollOffset = newScrollHeight - prevScrollHeight;
                container.scrollTop = prevScrollTop + scrollOffset;
                console.log(
                    `[ChatWindow] Poll update: Restoring scroll position. Offset: ${scrollOffset}, New scrollTop: ${container.scrollTop}`,
                );
            }
            // Else (user was scrolled up and no new messages added height), do nothing
            // -----------------------------

            scrollPositionRef.current = null; // Clear stored position after adjustment
        }
        // Depend on messages array reference, loading status transition to 'succeeded', and loading state
    }, [messages, loading]);

    // --- Effect for Intersection Observer ---
    useEffect(() => {
        const observerCallback = (entries: IntersectionObserverEntry[]) => {
            setMessageOpacities((prevOpacities) => {
                const newOpacities = { ...prevOpacities };
                let changed = false;
                entries.forEach((entry) => {
                    const targetId = (entry.target as HTMLElement).dataset.messageId;
                    if (targetId) {
                        // Map intersectionRatio (0-1) to opacity (e.g., 0.4-1.0 for more emphasis)
                        const newOpacity = 0.4 + entry.intersectionRatio * 0.6;
                        // Only update state if opacity actually changes to avoid infinite loops
                        if (newOpacities[targetId] !== newOpacity) {
                            newOpacities[targetId] = newOpacity;
                            changed = true;
                        }
                    }
                });
                return changed ? newOpacities : prevOpacities;
            });
        };

        // Options: root is the scroll container, threshold triggers callback at different visibility levels
        const observerOptions: IntersectionObserverInit = {
            root: messagesContainerRef.current,
            rootMargin: "0px",
            threshold: Array.from({ length: 21 }, (_, i) => i * 0.05), // Trigger ~every 5% visibility change
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        // Observe all current message elements that have a data-message-id
        const messageElements = messagesContainerRef.current?.querySelectorAll("[data-message-id]");
        if (messageElements) {
            messageElements.forEach((el) => observer.observe(el));
        }

        // Cleanup: Disconnect observer when component unmounts or messages change
        return () => {
            observer.disconnect();
        };
        // Rerun observer setup if messages array changes
    }, [messages]); // Dependency on messages array reference

    // --- Scroll event handler ---
    const handleScroll = useCallback(() => {
        const container = messagesContainerRef.current;
        if (container) {
            const { scrollTop, scrollHeight, clientHeight } = container;
            // Show button if user is scrolled up even slightly (more than 1px from bottom)
            const isScrolledUp = scrollHeight - scrollTop - clientHeight > 1; // Use 1px threshold
            setShowScrollDownButton(isScrolledUp);
        }
    }, []); // Empty dependency array, relies on ref

    // --- Handlers ---

    // Handler to scroll the container to the bottom
    const scrollToBottom = () => {
        const container = messagesContainerRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    };

    // Handler to go back to the conversations list (useful on mobile)
    const handleGoBack = () => {
        dispatch(setActiveConversation(null)); // Clear the active conversation
    };

    // --- Render Logic ---

    // If no conversation is selected, show a placeholder
    if (!activeConversationId) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    textAlign: "center",
                    p: 3,
                    color: "text.secondary", // Use theme color
                    backgroundColor: "background.paper", // Use theme background
                }}
            >
                <Typography variant="h6">Select a conversation</Typography>
                <Typography variant="body1">
                    Choose a chat from the list to view messages.
                </Typography>
            </Box>
        );
    }

    // Determine the display name and potentially subtitle for the header
    const getChatHeaderContent = () => {
        if (participantProfileLoading) {
            return { title: "Loading Info...", subtitle: null };
        }

        // If profile is available, use it for the title and subtitle
        if (participantProfile) {
            const title =
                `${participantProfile.firstName || "User"} ${participantProfile.lastName || ""}`.trim();

            // Add subtitle if 'about' exists in your profile data
            let subtitle = participantProfile.about || null;

            // check if the subtitle length is greater than 50 charcters
            // if it is, then truncate it to only max 50 characters and then ... at the end
            if (subtitle && subtitle.length > 50) {
                subtitle = subtitle.substring(0, 50) + "...";
            }

            // Return the title and subtitle
            return { title, subtitle };
        }

        // Fallback if profile isn't available for some reason
        return {
            title: "Chat",
            subtitle: null,
        };
    };

    const { title: chatHeaderName, subtitle: chatHeaderSubtitle } = getChatHeaderContent();

    return (
        <Box
            sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                height: "100%", // Take full height of its container
                // backgroundColor: "background.default", // Use theme background
                backgroundColor: "transparent", // Use theme background
                backdropFilter: "blur(6px)",
            }}
        >
            {/* Chat Header */}
            <AppBar
                position="static"
                color="default"
                elevation={1}
                sx={{ backgroundColor: "background.paper" }}
            >
                <Toolbar variant="dense">
                    {/* --- ADD MOBILE BACK BUTTON --- */}
                    {!isTabletUp && (
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="back to conversations"
                            onClick={handleGoBack}
                            sx={{ mr: 1 }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    {/* --- END MOBILE BACK BUTTON --- */}
                    {/* Add Avatar here */}
                    <Avatar
                        src={participantProfile?.photoUrl || undefined}
                        sx={{ mr: 1.5, width: 32, height: 32 }} // Add margin-right for spacing
                        onClick={() =>
                            participantProfile?.userId &&
                            navigate(`/profile/${participantProfile.userId}`)
                        }
                    >
                        {/* Fallback Icon */}
                        {!participantProfile?.photoUrl && <PersonIcon fontSize="small" />}
                    </Avatar>

                    {/* Existing Box for name and subtitle */}
                    <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                        {" "}
                        {/* Container for text */}
                        <Typography variant="h6" noWrap component="div">
                            {chatHeaderName}
                        </Typography>
                        {chatHeaderSubtitle && (
                            <Typography
                                variant="caption"
                                noWrap
                                display="block"
                                color="text.secondary"
                            >
                                {chatHeaderSubtitle}
                            </Typography>
                        )}
                    </Box>
                    {/* Add other header actions here if needed (e.g., info, call buttons) */}
                </Toolbar>
            </AppBar>

            {/* Message List Area */}
            <Box
                ref={messagesContainerRef} // Add ref to the scrollable container
                onScroll={handleScroll} // <-- Add scroll event handler
                sx={{
                    position: "relative", // Keep for potential absolute positioned children like scroll button
                    flexGrow: 1, // Ensures this Box takes up available space
                    overflowY: "auto", // Allows scrolling only when content overflows
                    p: 2,
                    display: "flex",
                    flexDirection: "column", // Stack messages vertically
                    // Note: backdropFilter and transparent background are handled by the parent Box
                    // Custom Scrollbar Styles
                    "&::-webkit-scrollbar": {
                        width: "10px", // Thickness of the scrollbar
                    },
                    "&::-webkit-scrollbar-track": {
                        // Use theme background or a light grey for the track
                        backgroundColor: (theme) => theme.palette.background.paper,
                        borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        // Use primary color for the thumb
                        backgroundColor: (theme) => theme.palette.primary.main,
                        borderRadius: "10px",
                        border: (theme) => `2px solid ${theme.palette.background.paper}`, // Optional border matching track
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                        // Darker shade on hover
                        backgroundColor: (theme) => theme.palette.primary.dark,
                    },
                }}
            >
                {loading === "pending" &&
                    messages.length === 0 && ( // Show loading only on initial load
                        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                            <CircularProgress />
                        </Box>
                    )}
                {error && ( // Show error if loading failed
                    <Alert severity="error" sx={{ m: 1 }}>
                        Error loading messages: {error}
                    </Alert>
                )}

                {/* Render the list of messages */}
                <List sx={{ width: "100%" }}>
                    {messages.map((message: Message, index) => {
                        const isCurrentUser = message.senderId === currentUser?.id;

                        // --- DEBUGGING LOG ---
                        console.log(
                            `Msg ID: ${message.id}, isCurrent: ${isCurrentUser}, Sender: ${message.senderId}, User: ${currentUser?.id}`,
                        );
                        // ---------------------

                        return (
                            <ListItem
                                key={message.id || index} // Use message.id if available, otherwise index
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: isCurrentUser ? "flex-end" : "flex-start", // Align messages left/right
                                    mb: 1, // Margin between messages
                                }}
                            >
                                <Paper
                                    // Add data-message-id attribute for the observer
                                    data-message-id={message.id}
                                    elevation={1}
                                    sx={{
                                        p: "4px 10px 4px 10px",
                                        borderRadius: isCurrentUser
                                            ? "20px 20px 5px 20px"
                                            : "20px 20px 20px 5px",
                                        border: `1px solid#9f8f5e !important`,
                                        // New background color logic
                                        backgroundColor: isCurrentUser
                                            ? muiTheme.palette.mode === "dark"
                                                ? "#9F8F5E"
                                                : "#FFECB3" // Dark Gold : Light Gold
                                            : muiTheme.palette.mode === "dark"
                                              ? "#424242"
                                              : "#EEEEEE", // Dark Grey : Light Grey

                                        // New text color logic (ensure contrast)
                                        color: isCurrentUser
                                            ? muiTheme.palette.mode === "dark"
                                                ? "#FFFFFF"
                                                : "#4A3B00" // White text on Dark Gold : Dark Amber text on Light Gold
                                            : muiTheme.palette.mode === "dark"
                                              ? "#FFFFFF"
                                              : "#212121", // White text on Dark Grey : Dark Grey text on Light Grey

                                        maxWidth: "75%",
                                        wordBreak: "break-word",
                                        transition: "opacity 0.2s ease-in-out", // Smooth transition for opacity
                                        // Apply dynamic opacity from state
                                        opacity: messageOpacities[message.id] ?? 1, // Default to 1
                                    }}
                                >
                                    <ListItemText
                                        primary={message.content}
                                        // Optionally add sender name for group chats later
                                    />
                                </Paper>
                                {/* Timestamp below the message bubble */}
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mt: 0.5,
                                        color: "text.secondary",
                                        alignSelf: isCurrentUser ? "flex-end" : "flex-start",
                                    }}
                                >
                                    {formatMessageTimestamp(message.createdAt)}
                                </Typography>
                            </ListItem>
                        );
                    })}
                    {/* Empty div at the end to help with scrolling */}
                    <div ref={messagesEndRef} />
                </List>
            </Box>

            {/* Message Input Area - Grouped into a non-shrinking footer Box */}
            <Box sx={{ flexShrink: 0, backgroundColor: "background.paper" }}>
                <Divider />
                <Box sx={{ p: 1 }}>
                    {/* MessageInput component handles the input field and send button logic */}
                    <MessageInput conversationId={activeConversationId} />
                </Box>
            </Box>

            {/* Scroll to Bottom Button */}
            {showScrollDownButton && (
                <Fab
                    color="primary"
                    size="small"
                    onClick={scrollToBottom}
                    sx={{
                        position: "absolute",
                        // Adjust positioning relative to the outer box
                        // Place it above the MessageInput area
                        bottom: 82, // Experiment with this value (e.g., height of MessageInput + margin)
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 10, // Ensure it's above other content
                    }}
                    aria-label="scroll to bottom"
                >
                    <KeyboardArrowDownIcon />
                </Fab>
            )}
        </Box>
    );
};

export default ChatWindow;
